/**
 * A list's view (filters, sort, search) kept in the address bar, so Refresh, Back and a shared link all
 * show the same view (PHP-3341). Each page names its params once in a schema; a missing or unknown value
 * reads as the param's default, and a default is left out of the URL so links stay short.
 *
 * Choices the customer clicks (a chip, a select, a sort) add a history entry, so Back steps back to the
 * previous view. Typed search text replaces the entry instead (`useUrlText`), so typing doesn't flood
 * the history. Params the schema doesn't name (another tab's, `?tab=` itself) are kept as they are.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router";

/** One value in the URL. Methods (not arrow properties) so a `Param<number>` fits a mixed schema. */
export interface Param<T> {
  key: string;
  fallback: T;
  /** The value `raw` stands for, or `undefined` when it isn't a valid one (the default is used). */
  parse(raw: string): T | undefined;
  /** How the value is written; an empty string removes the param. */
  format(value: T): string;
}

/** One of a fixed set of strings (a filter chip, a sort). */
export function choice<T extends string>(key: string, values: readonly T[], fallback: T): Param<T> {
  return {
    key,
    fallback,
    parse: (raw) => (values as readonly string[]).find((v) => v === raw) as T | undefined,
    format: (v) => v,
  };
}

/** Free text (a search box). Leading and trailing spaces never reach the URL. */
export function text(key: string): Param<string> {
  return { key, fallback: "", parse: (raw) => raw.trim(), format: (v) => v.trim() };
}

/** A whole number between `min` and `max`. */
export function count(key: string, fallback: number, min: number, max: number): Param<number> {
  return {
    key,
    fallback,
    parse: (raw) => {
      const n = Number(raw);
      return /^\d+$/.test(raw) && n >= min && n <= max ? n : undefined;
    },
    format: String,
  };
}

/** A calendar day, `YYYY-MM-DD`; `null` when there isn't one. */
export function day(key: string): Param<string | null> {
  return {
    key,
    fallback: null,
    parse: (raw) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return undefined;
      const d = new Date(`${raw}T00:00:00Z`);
      return !Number.isNaN(d.getTime()) && d.toISOString().startsWith(raw) ? raw : undefined;
    },
    format: (v) => v ?? "",
  };
}

// The method signatures above make `Param<T>` assignable here whatever its T.
type Schema = Record<string, Param<unknown>>;
export type View<S extends Schema> = { [K in keyof S]: S[K] extends Param<infer T> ? T : never };
export type SetView<S extends Schema> = (patch: Partial<View<S>>, options?: { replace?: boolean }) => void;

/** Read a schema's values from search params (exported for tests and non-hook callers). */
export function readView<S extends Schema>(params: URLSearchParams, schema: S): View<S> {
  const view: Record<string, unknown> = {};
  for (const [name, param] of Object.entries(schema)) {
    const raw = params.get(param.key);
    const value = raw === null ? undefined : param.parse(raw);
    view[name] = value === undefined ? param.fallback : value;
  }
  return view as View<S>;
}

/** Write `patch` into `params`: defaults and empty values are removed, other params are untouched. */
export function writeView<S extends Schema>(
  params: URLSearchParams,
  schema: S,
  patch: Partial<View<S>>,
): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const [name, value] of Object.entries(patch)) {
    const param = schema[name];
    if (!param) continue;
    const written = param.format(value);
    if (written === "" || written === param.format(param.fallback)) next.delete(param.key);
    else next.set(param.key, written);
  }
  return next;
}

/**
 * The view a schema describes, read from the URL, and a setter that changes part of it. Pass a schema
 * defined once at module level. The setter adds a history entry unless `{ replace: true }`.
 */
export function useUrlView<S extends Schema>(schema: S): [View<S>, SetView<S>] {
  const [params, setParams] = useSearchParams();
  const view = useMemo(() => readView(params, schema), [params, schema]);
  const setView = useCallback<SetView<S>>(
    (patch, options) => setParams((p) => writeView(p, schema, patch), { replace: options?.replace }),
    [setParams, schema],
  );
  return [view, setView];
}

/**
 * A search box backed by a URL value: the box updates as the customer types, and the URL follows after
 * a pause (replacing the history entry). When the URL changes on its own (Back, a link) the box shows it.
 */
export function useUrlText(urlValue: string, commit: (value: string) => void, delay = 300) {
  const [value, setValue] = useState(urlValue);
  const [seen, setSeen] = useState(urlValue);
  if (seen !== urlValue) {
    setSeen(urlValue);
    if (value.trim() !== urlValue) setValue(urlValue);
  }
  useEffect(() => {
    if (value.trim() === urlValue) return;
    const timer = setTimeout(() => commit(value.trim()), delay);
    return () => clearTimeout(timer);
  }, [value, urlValue, commit, delay]);
  return [value, setValue] as const;
}
